// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { SettingsService } from '../../shared/services/settings.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';

export const DATE_DIVIDER = ' – ';
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-spare-parts-datepicker',
  templateUrl: './spare-parts-datepicker.component.html',
  styleUrl: './spare-parts-datepicker.component.scss',
})
export class SparePartsDatepickerComponent implements OnChanges {
  dateRange = input<string>('');
  allowedYearsRange = input<number>(3);
  dateRangeChange = output<string>();

  public minPropertyToDatepicker = computed(() =>
    moment
      .utc()
      .subtract(this.allowedYearsRange(), 'years')
      .format(ISO_DATE_FORMAT),
  );

  protected readonly settingsServices = inject(SettingsService);
  protected readonly dateFormat = toSignal(this.settingsServices.dateFormat$);

  private readonly regexDateRange =
    /^[1-9]\d{3}-[0-1]\d-[0-3]\d – [1-9]\d{3}-[0-1]\d-[0-3]\d$/;

  private readonly localDateRangeFormat = signal({
    range: this.dateRange(),
    format: ISO_DATE_FORMAT,
  });

  private readonly isoCurrentDateRange = computed(() => {
    const rangeFormat = this.localDateRangeFormat();
    if (rangeFormat) {
      const { range, format } = rangeFormat;
      return this.dateMoment(range, format);
    }
  });

  private readonly isoCurrentDateRangeString = computed(() => {
    let range = '';
    const isoDate = this.isoCurrentDateRange();
    if (isoDate) {
      const { start, end } = isoDate;
      const s = start.format(ISO_DATE_FORMAT);
      const e = end.format(ISO_DATE_FORMAT);
      range = `${s}${DATE_DIVIDER}${e}`;
    }
    return range;
  });

  protected readonly currentDateRange = computed(() => {
    const dFormat = this.dateFormat();
    const isoDateRange = this.isoCurrentDateRange();
    if (isoDateRange && dFormat) {
      const dateFormat = dFormat.longFormat.toUpperCase();
      const start = isoDateRange.start.format(dateFormat);
      const end = isoDateRange.end.format(dateFormat);

      return `${start}${DATE_DIVIDER}${end}`;
    }

    return '';
  });

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateRange']?.currentValue) {
      if (this.regexDateRange.test(this.dateRange())) {
        this.localDateRangeFormat.set({
          range: this.dateRange(),
          format: ISO_DATE_FORMAT,
        });
      } else {
        throw new Error(
          `Date range should be in this format: '${ISO_DATE_FORMAT}${DATE_DIVIDER}${ISO_DATE_FORMAT}'`,
        );
      }
    }
  }

  protected onDateRangeChange(dateRange: string): void {
    const dFormat = this.dateFormat();
    if (dFormat) {
      this.localDateRangeFormat.set({
        range: dateRange,
        format: dFormat.longFormat.toUpperCase(),
      });
    }

    this.dateRangeChange.emit(this.isoCurrentDateRangeString());
  }

  private readonly dateMoment = (dateRange: string, format: string) => {
    if (dateRange) {
      const [start, end] = dateRange.split(DATE_DIVIDER);
      const mStart = moment.utc(start, format, true);
      const mEnd = moment.utc(end, format, true);

      return { start: mStart, end: mEnd };
    }
  };
}
